import type { Contact } from '../../services/api'
import { normalizeCompanyName } from '../../utils/companyName'
import { CONNECTION_TYPE_OPTIONS, DEFAULT_CONNECTION_TYPE } from '../../constants/networking'

// Kept free of React and MUI so the matching rules can be unit tested: the
// test runner has no DOM, and loading the icon barrel exhausts file handles on
// Windows. WarmContactsCell re-exports these, so importers are unaffected.

// MWA-237/254 — "warm contacts" = the client's network at a given company (people
// who can make an introduction). We match a contact to a company by normalized
// name (reusing the MWA-257 normalizer) so "Equinix" matches "Equinix, Inc.".
export function warmContactsAt(contacts: Contact[], company: string | undefined | null): Contact[] {
  const key = normalizeCompanyName(company || '')
  if (!key) return []
  return contacts.filter((c) => !c.isDeleted && normalizeCompanyName(c.company) === key)
}

// MWA-297 — every contact on an opportunity: the ones who work there, plus the
// ones staff linked by hand. The second set is how a referrer appears — somebody
// who works elsewhere but opened the door — which the employer match can never
// find, because it asks where a person works rather than who they know.
//
// `linkedContactIds` is optional so the target-companies surface, which has a
// company name and no opportunity behind it, keeps calling this unchanged.
// Order is employer-match first, then links, so the automatic result stays
// stable when nobody has linked anything.
export function contactsForJob(
  contacts: Contact[],
  company: string | undefined | null,
  linkedContactIds?: string[]
): Contact[] {
  const matched = warmContactsAt(contacts, company)
  if (!linkedContactIds?.length) return matched
  const seen = new Set(matched.map((c) => c.id))
  const linked = linkedContactIds
    .map((id) => contacts.find((c) => c.id === id && !c.isDeleted))
    .filter((c): c is Contact => c !== undefined && !seen.has(c.id))
  return [...matched, ...linked]
}

// MWA-358 — the order a long employer match is read in, since only the first
// few rows are shown: contacts the coach flagged as priority, then the ones
// already handed to the client, then the closest connections, then by name. An
// absent connection level reads as the default, the same way the networking
// table reads it, so an older contact is not pushed to the bottom for lacking one.
export function orderWarmContacts(contacts: Contact[]): Contact[] {
  const levelRank = (c: Contact) => {
    const i = CONNECTION_TYPE_OPTIONS.findIndex(
      (o) => o.value === (c.connectionType || DEFAULT_CONNECTION_TYPE)
    )
    return i === -1 ? CONNECTION_TYPE_OPTIONS.length : i
  }
  return [...contacts].sort(
    (a, b) =>
      Number(Boolean(b.isPriority)) - Number(Boolean(a.isPriority)) ||
      Number(Boolean(b.reachOutAt)) - Number(Boolean(a.reachOutAt)) ||
      levelRank(a) - levelRank(b) ||
      contactDisplayName(a).localeCompare(contactDisplayName(b))
  )
}

export function contactDisplayName(c: Contact): string {
  return c.fullName || `${c.firstName || ''} ${c.lastName || ''}`.trim() || c.email || 'Unnamed contact'
}
